import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SHELL_AGENT_PARAM,
  pushSharedUrl,
  replaceSharedUrl,
} from '@robinpowered/dashboard-apps-common';
import {
  applyProfileSidebarParams,
  parseProfileSidebarParams,
  PROFILE_USER_PARAM,
  PROFILE_VIEW_PARAM,
  ProfileSidebarUrlState,
  ProfileSidebarView,
} from 'utils/profileSidebarParams';

export type ProfileSidebarUrlControls = ProfileSidebarUrlState & {
  /** Open (or switch) the profile sidebar on a user. */
  openProfile: (userId: string) => void;
  /** Close the sidebar, clearing both params. */
  closeProfile: () => void;
  /** Switch between the compact drawer and the expanded full-page layout. */
  setView: (view: ProfileSidebarView) => void;
};

/**
 * Single source of truth for the profile sidebar's open/expanded state, held
 * in the URL (`profile_user` / `profile_view`) rather than React state so the
 * panel survives a refresh and can be shared as a link.
 */
export const useProfileSidebarUrlState = (): ProfileSidebarUrlControls => {
  const [searchParams] = useSearchParams();
  const { userId, view } = useMemo(
    () => parseProfileSidebarParams(searchParams),
    [searchParams]
  );

  // Reads live `window.location` and writes via the shared-navigation helpers
  // rather than `setSearchParams`, so params owned by MFE Routers survive and
  // see our write. Rationale: `EventOverlayHost`'s `setEventParam`.
  const commit = useCallback(
    (
      next: ProfileSidebarUrlState,
      mode: 'push' | 'replace',
      { takeSlot = false }: { takeSlot?: boolean } = {}
    ) => {
      const params = new URLSearchParams(window.location.search);
      applyProfileSidebarParams(params, next);
      if (takeSlot) {
        // Last-opened-wins across the shell's right-hand surfaces: an opening
        // profile closes the assistant panel so the two never show together.
        // Its parcel stays mounted, so the conversation survives a reopen.
        // The inverse (assistant opening closes the profile) lives in
        // AgentChatSidebar's agentChatParam.
        params.delete(SHELL_AGENT_PARAM);
      }
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
      (mode === 'push' ? pushSharedUrl : replaceSharedUrl)(url);
    },
    []
  );

  // Only opening from closed pushes, so a single Back always leaves the
  // profile no matter how many people were viewed: switching to another
  // person replaces the open profile's entry rather than stacking one per
  // visit. Re-opening whoever is already open is a no-op — search can select
  // the person whose profile is open behind it.
  //
  // The view carries over from an already-open profile; switching people
  // shouldn't collapse it.
  const openProfile = useCallback(
    (nextUserId: string) => {
      const current = parseProfileSidebarParams(window.location.search);
      if (current.userId === nextUserId) {
        return;
      }
      const isOpeningFromClosed = current.userId === null;
      commit(
        {
          userId: nextUserId,
          view: isOpeningFromClosed ? 'compact' : current.view,
        },
        isOpeningFromClosed ? 'push' : 'replace',
        { takeSlot: true }
      );
    },
    [commit]
  );

  // Replaces — a dismissed panel isn't an entry to Back through. No-op when
  // the params are already gone, so closing can't clobber an entry another
  // navigation just pushed (search selecting a map result navigates *and*
  // closes).
  const closeProfile = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(PROFILE_USER_PARAM) && !params.has(PROFILE_VIEW_PARAM)) {
      return;
    }
    commit({ userId: null, view: 'compact' }, 'replace');
  }, [commit]);

  // Also replaces: a presentation change, not a new destination.
  const setView = useCallback(
    (nextView: ProfileSidebarView) => {
      const current = parseProfileSidebarParams(window.location.search);
      if (current.userId === null || current.view === nextView) {
        return;
      }
      commit({ userId: current.userId, view: nextView }, 'replace');
    },
    [commit]
  );

  return { userId, view, openProfile, closeProfile, setView };
};
